import { useEffect, useState } from 'react';
import { D, formatMoney } from '@makila/fiscal';
import { SIZE_GRIDS } from '@makila/pos-core';
import { Grid3x3, Plus, Shirt, X } from 'lucide-react';
import clsx from 'clsx';
import { api, patch, post } from './api';
import { printHtml } from '../lib/receipt';
import { navigate } from './navigate';
import { Alert, Badge, Button, Card, Dialog, EmptyState, Field, Input, MoneyInput, Select, Skeleton, Toggle, useToast } from './ui';
import { attempt, useLoad } from './useLoad';

interface Cell { id: string; code: string; name: string; barcode: string | null; unitPrice: string; active: boolean; size: string | null; color: string | null; label: string | null; stock: Record<string, string> }
interface Matrix { model: { id: string; code: string; name: string; unitPrice: string; sizes: string[]; colors: string[]; collection: string | null }; stores: { id: string; code: string; name: string; kind: string }[]; cells: Cell[] }

const q = (v: string | undefined) => (v ? new D(v).toString().replace('.', ',') : '0');
const money = (v: string) => v.trim().replace(/\s/g, '').replace(',', '.');
const eq = (a: string | null, b: string | null) => (a ?? '').toLocaleLowerCase('pt') === (b ?? '').toLocaleLowerCase('pt');

/** Lista editável de pastilhas (tamanhos, cores): escreve-se e Enter junta. */
function Chips({ label, values, onChange, placeholder, max }: { label: string; values: string[]; onChange: (v: string[]) => void; placeholder: string; max: number }) {
  const [v, setV] = useState('');
  const add = () => {
    const x = v.trim();
    if (x && !values.some((y) => eq(y, x))) onChange([...values, x.slice(0, max)]);
    setV('');
  };
  return (
    <Field label={label} hint="Escreva e carregue em Enter; a ordem é a dos botões na caixa">
      <div className="flex min-h-11 flex-wrap items-center gap-1.5 rounded-lg border border-base bg-[var(--surface)] p-1.5">
        {values.map((x) => (
          <span key={x} className="inline-flex h-8 items-center gap-1 rounded-full bg-surface-2 pl-3 pr-1 text-sm font-medium">
            {x}
            <button type="button" aria-label={`Tirar ${x}`} onClick={() => onChange(values.filter((y) => y !== x))} className="grid h-6 w-6 place-items-center rounded-full text-muted hover:bg-[var(--surface)]"><X size={14} /></button>
          </span>
        ))}
        <input aria-label={label} value={v} placeholder={placeholder} onChange={(e) => setV(e.target.value)} onBlur={add}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); add(); } }}
          className="h-8 min-w-24 flex-1 bg-transparent px-2 text-sm outline-none" />
      </div>
    </Field>
  );
}

/**
 * Grelha de tamanhos e cores de um modelo (docs/14 Fase 4): cria de uma vez
 * as variantes que faltam e mostra, em cada célula, o stock por loja e o preço.
 */
export function MatrixDialog({ productId, onClose }: { productId: string; onClose: (changed: boolean) => void }) {
  const toast = useToast();
  const [m, setM] = useState<Matrix | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [sizes, setSizes] = useState<string[]>([]);
  const [colors, setColors] = useState<string[]>([]);
  const [price, setPrice] = useState('');
  const [barcodes, setBarcodes] = useState(true);
  const [store, setStore] = useState('');
  const [prices, setPrices] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [changed, setChanged] = useState(false);
  const load = async () => {
    try {
      const r = await api<Matrix>(`/products/${productId}/matrix`);
      setM(r); setSizes(r.model.sizes); setColors(r.model.colors); setPrices({});
      if (!price) setPrice(new D(r.model.unitPrice).toFixed(2).replace('.', ','));
    } catch (e) { setErr((e as Error).message); }
  };
  useEffect(() => { void load(); }, [productId]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!m) return <Dialog title="Grelha de tamanhos e cores" size="lg" onClose={() => onClose(false)}>{err ? <Alert>{err}</Alert> : <Skeleton rows={6} />}</Dialog>;

  const gridCells = m.cells.filter((c) => c.size || c.color);
  const saved = (sizes.join('|') === m.model.sizes.join('|')) && (colors.join('|') === m.model.colors.join('|'));
  const newCount = (colors.length ? colors : [null]).flatMap((c) => (sizes.length ? sizes : [null]).map((s) => [s, c] as const))
    .filter(([s, c]) => (s || c) && !gridCells.some((x) => eq(x.size, s) && eq(x.color, c))).length;
  const stockOf = (c: Cell) => (store ? q(c.stock[store]) : q(Object.values(c.stock).reduce((a, x) => a.plus(x), new D(0)).toString()));
  const rows = m.model.colors.length ? m.model.colors : [null];
  const cols = m.model.sizes.length ? m.model.sizes : [null];
  const cellAt = (s: string | null, c: string | null) => gridCells.find((x) => eq(x.size, s) && eq(x.color, c));
  const dirtyPrices = Object.entries(prices).filter(([id, v]) => { const c = m.cells.find((x) => x.id === id); return c && /^\d+([.,]\d{1,2})?$/.test(v.trim()) && !new D(money(v)).equals(c.unitPrice); });

  const saveGrid = async () => {
    setBusy(true);
    const e = await attempt(async () => {
      const r = await api<{ created: number; deactivated: number; reactivated: number }>(`/products/${productId}/matrix`, { method: 'PUT', body: JSON.stringify({ sizes, colors, price: money(price || '0'), barcodes }) });
      toast(`${r.created} variante(s) criada(s)${r.deactivated ? `, ${r.deactivated} desactivada(s)` : ''}${r.reactivated ? `, ${r.reactivated} reactivada(s)` : ''}`);
    });
    setErr(e); setBusy(false);
    if (!e) { setChanged(true); await load(); }
  };
  const savePrices = async () => {
    setBusy(true);
    const e = await attempt(() => patch(`/products/${productId}/matrix/prices`, { prices: dirtyPrices.map(([variantId, v]) => ({ variantId, price: money(v) })) }));
    setErr(e); setBusy(false);
    if (!e) { toast('Preços gravados'); setChanged(true); await load(); }
  };

  return (
    <Dialog title={`Grelha — ${m.model.name}`} size="lg" onClose={() => onClose(changed)}
      footer={<Button variant="ghost" onClick={() => onClose(changed)}>Fechar</Button>}>
      <div className="space-y-5">
        {err && <Alert>{err}</Alert>}
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Chips label="Tamanhos" values={sizes} onChange={setSizes} placeholder="S, M, 38…" max={20} />
            <div className="flex flex-wrap gap-1.5">
              {SIZE_GRIDS.map((g) => <button key={g.key} type="button" onClick={() => setSizes([...g.sizes])} className="h-8 rounded-full border border-base px-3 text-xs font-medium hover:border-brand-500">{g.label}</button>)}
            </div>
          </div>
          <Chips label="Cores" values={colors} onChange={setColors} placeholder="Preto, Azul…" max={30} />
        </div>
        {!saved && (
          <div className="flex flex-wrap items-end gap-3 rounded-xl bg-surface-2 p-4">
            {newCount > 0 && <Field label="Preço das novas"><MoneyInput value={price} onChange={(e) => setPrice(e.target.value)} /></Field>}
            {newCount > 0 && <div className="pb-1"><Toggle checked={barcodes} onChange={setBarcodes} label="Código de barras interno" description="Para imprimir as etiquetas" /></div>}
            <Button icon={Grid3x3} disabled={busy || (!sizes.length && !colors.length)} onClick={() => void saveGrid()}>
              {newCount ? `Criar ${newCount} variante${newCount === 1 ? '' : 's'}` : 'Gravar a grelha'}
            </Button>
          </div>
        )}
        {gridCells.length > 0 && (
          <div className="space-y-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h3 className="font-display font-semibold">Stock e preço</h3>
              {m.stores.length > 1 && (
                <Select aria-label="Loja" value={store} onChange={(e) => setStore(e.target.value)} className="w-56">
                  <option value="">Todas as lojas</option>
                  {m.stores.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </Select>
              )}
            </div>
            <div className="overflow-x-auto rounded-xl border border-base">
              <table className="w-full text-sm">
                <thead className="bg-surface-2 text-xs uppercase tracking-wide text-muted">
                  <tr><th className="sticky left-0 bg-surface-2 px-3 py-2 text-left">{m.model.colors.length ? 'Cor' : ''}</th>{cols.map((s) => <th key={s ?? '-'} className="px-2 py-2 text-center">{s ?? '—'}</th>)}</tr>
                </thead>
                <tbody className="divide-y divide-[var(--border)]">
                  {rows.map((c) => (
                    <tr key={c ?? '-'}>
                      <th className="sticky left-0 bg-[var(--surface)] px-3 py-2 text-left font-medium">{c ?? 'Preço'}</th>
                      {cols.map((s) => {
                        const x = cellAt(s, c);
                        if (!x) return <td key={s ?? '-'} className="px-2 py-2 text-center text-muted">—</td>;
                        const n = Number(stockOf(x).replace(',', '.'));
                        return (
                          <td key={s ?? '-'} className={clsx('px-1.5 py-1.5 text-center align-top', !x.active && 'opacity-40')}>
                            <div className={clsx('num text-base font-semibold', n <= 0 ? 'text-muted' : n < 2 ? 'text-amber-600' : '')} title={`Stock de ${x.label}`}>{stockOf(x)}</div>
                            <input aria-label={`Preço ${x.label}`} inputMode="decimal" disabled={!x.active}
                              value={prices[x.id] ?? new D(x.unitPrice).toFixed(2).replace('.', ',')} onChange={(e) => setPrices({ ...prices, [x.id]: e.target.value })}
                              className="num mt-1 h-8 w-24 rounded-md border border-base bg-[var(--surface)] px-2 text-right text-xs" />
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-muted">
              <span>Número grande: unidades em stock{store ? '' : ' (todas as lojas)'}. As células esbatidas saíram da grelha e não aparecem na caixa.</span>
              {dirtyPrices.length > 0 && <Button size="sm" disabled={busy} onClick={() => void savePrices()}>Gravar {dirtyPrices.length} preço{dirtyPrices.length === 1 ? '' : 's'}</Button>}
            </div>
          </div>
        )}
      </div>
    </Dialog>
  );
}

interface Collection { collection: string; models: number; stock: string; sold: string; revenue: string; last_sale: string | null; sales: { id: string; name: string; percent: string; endsOn: string | null }[] }
const dmy = (d: string | null) => (d ? d.split('-').reverse().join('/') : '—');

/** Colecções: o que resta em stock e o que se vendeu; "Pôr em saldo" cria a promoção da colecção. */
export function CollectionsPage() {
  const { data, error, reload } = useLoad<{ items: Collection[]; period: { from: string; to: string } }>('/collections');
  const [sale, setSale] = useState<{ collection: string; percent: string; endsOn: string } | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const toast = useToast();
  if (error) return <Alert>{error}</Alert>;
  if (!data) return <Skeleton rows={5} />;
  const start = async () => {
    if (!sale) return;
    const e = await attempt(() => post('/promotions', { name: `Saldos ${sale.collection}`.slice(0, 80), kind: 'percent', percent: sale.percent.replace(',', '.'), collections: [sale.collection], endsOn: sale.endsOn || null }));
    setErr(e);
    if (!e) { toast('Saldos em vigor: as caixas aplicam-nos na sincronização seguinte'); setSale(null); void reload(); }
  };
  return (
    <div className="max-w-5xl space-y-4">
      {err && <Alert>{err}</Alert>}
      <Card title="Colecções" description={`Vendas de ${dmy(data.period.from)} a ${dmy(data.period.to)} (90 dias). A colecção indica-se na ficha do modelo e passa para as variantes.`} padded={false}>
        {!data.items.length ? (
          <EmptyState icon={Shirt} title="Ainda não há colecções" text='Na ficha do artigo, escreva a colecção (ex.: "Verão 2026").' action={<Button onClick={() => navigate('/gestao/artigos')}>Ir aos artigos</Button>} />
        ) : (
          <div className="overflow-x-auto p-2 xl:p-3">
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase tracking-wide text-muted">
                <tr><th className="px-2 py-2">Colecção</th><th className="px-2 text-right">Modelos</th><th className="px-2 text-right">Em stock</th><th className="px-2 text-right">Vendidas</th><th className="hidden px-2 text-right sm:table-cell">Vendas (MT)</th><th className="hidden px-2 md:table-cell">Última venda</th><th className="px-2" /></tr>
              </thead>
              <tbody className="divide-y divide-[var(--border)]">
                {data.items.map((c) => (
                  <tr key={c.collection}>
                    <td className="px-2 py-3">
                      <div className="font-medium">{c.collection}</div>
                      {c.sales.map((s) => <Badge key={s.id} tone="brand">−{s.percent.replace('.', ',')}%{s.endsOn ? ` até ${dmy(s.endsOn)}` : ''}</Badge>)}
                    </td>
                    <td className="num px-2 text-right">{c.models}</td>
                    <td className="num px-2 text-right">{q(c.stock)}</td>
                    <td className="num px-2 text-right">{q(c.sold)}</td>
                    <td className="num hidden px-2 text-right sm:table-cell">{formatMoney(c.revenue)}</td>
                    <td className="hidden px-2 md:table-cell">{dmy(c.last_sale)}</td>
                    <td className="px-2 text-right">{!c.sales.length && <Button variant="ghost" size="sm" onClick={() => setSale({ collection: c.collection, percent: '30', endsOn: '' })}>Pôr em saldo</Button>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
      {sale && (
        <Dialog title={`Saldos — ${sale.collection}`} size="sm" onClose={() => setSale(null)}
          footer={<><Button variant="ghost" onClick={() => setSale(null)}>Cancelar</Button><Button onClick={() => void start()}>Começar os saldos</Button></>}>
          <div className="space-y-4">
            <Field label="Desconto"><Input inputMode="decimal" className="w-28" value={sale.percent} onChange={(e) => setSale({ ...sale, percent: e.target.value })} /></Field>
            <Field label="Até (opcional)" hint="Sem data, ficam até os terminar em Promoções"><Input type="date" className="w-44" value={sale.endsOn} onChange={(e) => setSale({ ...sale, endsOn: e.target.value })} /></Field>
            <p className="text-sm text-muted">Todas as peças desta colecção, em todas as lojas, a partir da próxima sincronização das caixas.</p>
          </div>
        </Dialog>
      )}
    </div>
  );
}

interface StockRequest { id: string; status: 'open' | 'sent' | 'refused'; quantity: string; note: string | null; reason: string | null; requested_at: string; closed_at: string | null; code: string; name: string; from_name: string; to_name: string; operator: string | null; transfer_number: number | null; available: string }

/** Pedidos das caixas a outra loja: enviar (transferência com guia) ou recusar. */
export function StockRequests({ onSent }: { onSent?: () => void }) {
  const open = useLoad<{ items: StockRequest[] }>('/stock/requests');
  const done = useLoad<{ items: StockRequest[] }>('/stock/requests?status=closed');
  const [refuse, setRefuse] = useState<{ id: string; reason: string } | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const toast = useToast();
  if (open.error) return <Alert>{open.error}</Alert>;
  if (!open.data) return <Skeleton rows={3} />;
  const when = (iso: string) => new Date(iso).toLocaleString('pt-MZ', { timeZone: 'Africa/Maputo', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
  const send = async (r: StockRequest) => {
    const e = await attempt(async () => {
      const x = await post<{ transfer: { number: number; guide: string } }>(`/stock/requests/${r.id}/send`, {});
      toast(`Transferência n.º ${x.transfer.number} enviada (guia ${x.transfer.guide})`);
    });
    setErr(e);
    if (!e) { void open.reload(); void done.reload(); onSent?.(); }
  };
  return (
    <div className="space-y-4">
      {err && <Alert>{err}</Alert>}
      <Card title="Pedidos por enviar" description="A caixa viu a peça no stock de outra loja e pediu-a. Enviar faz a transferência com guia de remessa; a loja que pediu recebe-a em Transferências." padded={false}>
        {!open.data.items.length ? <p className="p-6 text-center text-muted">Sem pedidos por enviar.</p> : (
          <ul className="divide-y divide-[var(--border)]">
            {open.data.items.map((r) => {
              const short = new D(r.available).lessThan(r.quantity);
              return (
                <li key={r.id} className="flex flex-wrap items-center gap-3 px-4 py-3 xl:px-6">
                  <div className="min-w-0 flex-1">
                    <div className="font-medium">{q(r.quantity)} × {r.name} <span className="font-mono text-xs text-muted">{r.code}</span></div>
                    <div className="text-sm text-muted">{r.from_name} → {r.to_name} · {when(r.requested_at)}{r.operator ? ` · ${r.operator}` : ''}{r.note ? ` · ${r.note}` : ''}</div>
                    <div className={clsx('text-xs', short ? 'text-brand-700' : 'text-muted')}>Em {r.from_name}: {q(r.available)}</div>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => setRefuse({ id: r.id, reason: '' })}>Recusar</Button>
                  <Button size="sm" icon={Plus} onClick={() => void send(r)}>Enviar</Button>
                </li>
              );
            })}
          </ul>
        )}
      </Card>
      {!!done.data?.items.length && (
        <Card title="Tratados" padded={false}>
          <ul className="divide-y divide-[var(--border)]">
            {done.data.items.slice(0, 20).map((r) => (
              <li key={r.id} className="flex flex-wrap items-center gap-3 px-4 py-2.5 text-sm xl:px-6">
                <span className="min-w-0 flex-1">{q(r.quantity)} × {r.name} <span className="text-muted">· {r.from_name} → {r.to_name}</span></span>
                {r.status === 'sent' ? <Badge tone="ok">enviado · transf. {r.transfer_number}</Badge> : <Badge>recusado{r.reason ? `: ${r.reason}` : ''}</Badge>}
              </li>
            ))}
          </ul>
        </Card>
      )}
      {refuse && (
        <Dialog title="Recusar o pedido" size="sm" onClose={() => setRefuse(null)}
          footer={<><Button variant="ghost" onClick={() => setRefuse(null)}>Cancelar</Button><Button disabled={refuse.reason.trim().length < 3} onClick={async () => {
            const e = await attempt(() => post(`/stock/requests/${refuse.id}/refuse`, { reason: refuse.reason }));
            setErr(e); setRefuse(null);
            if (!e) { void open.reload(); void done.reload(); }
          }}>Recusar</Button></>}>
          <Field label="Motivo"><Input autoFocus value={refuse.reason} maxLength={200} onChange={(e) => setRefuse({ ...refuse, reason: e.target.value })} placeholder="Já não há; reservada para outro cliente…" /></Field>
        </Dialog>
      )}
    </div>
  );
}

interface ReservationRow {
  id: string; code: string; customer_name: string; customer_nuit: string; customer_phone: string | null; items: { description: string; quantity: string }[];
  total: string; paid: string; rest: string; due_date: string; status: 'open' | 'collected' | 'cancelled'; refunded: boolean | null; close_reason: string | null;
  created_at: string; closed_at: string | null; store_name: string; collected_number: string | null; advances: string[]; overdue: boolean;
}

/** Reservas com sinal: por levantar (fora de prazo à cabeça) e as fechadas. Levantam-se e anulam-se na caixa. */
export function ReservationsPage() {
  const [tab, setTab] = useState<'open' | 'closed'>('open');
  const { data, error } = useLoad<{ items: ReservationRow[] }>(`/reservations?status=${tab}`);
  const items = data?.items ?? [];
  const late = items.filter((r) => r.overdue).length;
  const sum = (k: 'paid' | 'rest') => items.reduce((s, r) => s.plus(r[k]), new D(0)).toString();
  return (
    <div className="max-w-5xl space-y-4">
      <div role="tablist" className="flex gap-1">
        {([['open', 'Por levantar'], ['closed', 'Fechadas']] as const).map(([k, l]) => (
          <button key={k} role="tab" aria-selected={tab === k} onClick={() => setTab(k)} className={clsx('h-11 rounded-lg px-4 font-medium', tab === k ? 'bg-brand-700 text-white' : 'border border-base')}>{l}</button>
        ))}
      </div>
      {error && <Alert>{error}</Alert>}
      {!data ? <Skeleton rows={4} /> : (
        <Card title={tab === 'open' ? `${items.length} por levantar` : 'Fechadas'} padded={false}
          description={tab === 'open' ? `Recebido ${formatMoney(sum('paid'))} MT em sinais; falta ${formatMoney(sum('rest'))} MT.${late ? ` ${late} fora de prazo.` : ''} Levantar, receber mais ou anular faz-se na caixa (Reservas).` : 'As últimas 200.'}>
          {!items.length ? <p className="p-6 text-center text-muted">{tab === 'open' ? 'Não há reservas por levantar.' : 'Ainda não há reservas fechadas.'}</p> : (
            <ul className="divide-y divide-[var(--border)]">
              {items.map((r) => (
                <li key={r.id} className="flex flex-wrap items-start gap-3 px-4 py-3 xl:px-6">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-baseline gap-x-2">
                      <span className="font-mono font-bold tracking-wider">{r.code}</span>
                      <span className="font-medium">{r.customer_name}</span>
                      <span className="text-xs text-muted">NUIT {r.customer_nuit}{r.customer_phone ? ` · ${r.customer_phone}` : ''}</span>
                    </div>
                    <div className="text-sm text-muted">{r.items.map((i) => `${q(i.quantity)} × ${i.description}`).join(' · ')}</div>
                    <div className="text-xs text-muted">{r.store_name} · adiantamentos {r.advances.join(', ') || '—'}{r.collected_number ? ` · levantada em ${r.collected_number}` : ''}{r.close_reason ? ` · ${r.close_reason}` : ''}</div>
                  </div>
                  <div className="num text-right text-sm">
                    <div>Total <b>{formatMoney(r.total)}</b> · pago <b>{formatMoney(r.paid)}</b></div>
                    {r.status === 'open'
                      ? <div className={r.overdue ? 'font-semibold text-brand-700' : 'text-muted'}>falta {formatMoney(r.rest)} · {r.overdue ? 'fora de prazo desde' : 'até'} {dmy(r.due_date)}</div>
                      : <div>{r.status === 'collected' ? <Badge tone="ok">levantada</Badge> : <Badge tone="warn">anulada · {r.refunded ? 'sinal devolvido' : 'sinal ficou'}</Badge>}</div>}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>
      )}
    </div>
  );
}

interface Consignor { id: string; name: string; nuit: string | null; phone: string | null; commission_pct: string; note: string | null; active: boolean; products: number; settled_until: string | null }
interface ConsLine { productId: string; code: string; name: string; pct: string; quantity: string; gross: string; net: string; storeShare: string; due: string; stock?: string }
interface ConsReport { openFrom: string; from: string; to: string; lines: ConsLine[]; totals: { gross: string; net: string; storeShare: string; due: string }; consignor: { name: string; nuit: string | null } }
interface Settlement { id: string; number: number; period_from: string; period_to: string; lines: ConsLine[]; gross: string; net: string; store_share: string; due: string; reference: string | null; note: string | null; created_at: string; created_by_name: string | null }

/** Acerto em A4 para entregar ao fornecedor (é um relatório, não um documento fiscal). */
function settlementHtml(company: string, c: { name: string; nuit: string | null }, s: { number?: number; from: string; to: string; lines: ConsLine[]; totals: ConsReport['totals']; reference?: string | null }) {
  const e = (x: string) => x.replace(/[&<>"]/g, (ch) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[ch]!);
  const rows = s.lines.filter((l) => !new D(l.quantity).isZero()).map((l) => `<tr><td>${e(l.code)}</td><td>${e(l.name)}</td><td class="r">${q(l.quantity)}</td><td class="r">${formatMoney(l.gross)}</td><td class="r">${formatMoney(l.net)}</td><td class="r">${l.pct.replace('.', ',')}%</td><td class="r">${formatMoney(l.storeShare)}</td><td class="r"><b>${formatMoney(l.due)}</b></td></tr>`).join('');
  return `<!doctype html><html lang="pt"><head><meta charset="utf-8"><title>Acerto de consignação</title><style>
    @page { size: A4; margin: 15mm; } body { font: 11px/1.4 system-ui, sans-serif; color: #000; } h1 { font-size: 18px; margin: 0 0 4px; }
    table { width: 100%; border-collapse: collapse; margin-top: 12px; } th, td { border-bottom: 1px solid #ccc; padding: 4px; text-align: left; } .r { text-align: right; white-space: nowrap; }
    tfoot td { font-weight: 700; border-top: 2px solid #000; } .sign { margin-top: 30mm; display: flex; gap: 20mm; } .sign div { flex: 1; border-top: 1px solid #000; padding-top: 4px; }
  </style></head><body>
    <h1>Acerto de consignação${s.number ? ` n.º ${s.number}` : ' (por fechar)'}</h1>
    <div>${e(company)} · fornecedor <b>${e(c.name)}</b>${c.nuit ? ` (NUIT ${e(c.nuit)})` : ''}</div>
    <div>Período de ${dmy(s.from)} a ${dmy(s.to)}${s.reference ? ` · referência ${e(s.reference)}` : ''}</div>
    <table><thead><tr><th>Código</th><th>Peça</th><th class="r">Qtd.</th><th class="r">Vendido (c/ IVA)</th><th class="r">Sem IVA</th><th class="r">Loja</th><th class="r">Parte da loja</th><th class="r">A pagar</th></tr></thead>
    <tbody>${rows || '<tr><td colspan="8">Nada vendido no período.</td></tr>'}</tbody>
    <tfoot><tr><td colspan="3">Total</td><td class="r">${formatMoney(s.totals.gross)}</td><td class="r">${formatMoney(s.totals.net)}</td><td></td><td class="r">${formatMoney(s.totals.storeShare)}</td><td class="r">${formatMoney(s.totals.due)}</td></tr></tfoot></table>
    <p>A parte da loja calcula-se sobre o valor sem IVA. As devoluções do período estão abatidas. O fornecedor factura à loja o valor a pagar.</p>
    <div class="sign"><div>Pela loja</div><div>O fornecedor</div></div>
  </body></html>`;
}

/** Consignação: fornecedores, o acerto por fechar (desde o último) e os acertos feitos. */
export function ConsignmentPage() {
  const list = useLoad<{ items: Consignor[] }>('/consignors');
  const [sel, setSel] = useState<string | null>(null);
  const [edit, setEdit] = useState<{ id: string | null; name: string; nuit: string; phone: string; pct: string; note: string } | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const toast = useToast();
  useEffect(() => { if (!sel && list.data?.items[0]) setSel(list.data.items[0].id); }, [list.data, sel]);
  if (list.error) return <Alert>{list.error}</Alert>;
  if (!list.data) return <Skeleton rows={4} />;
  const save = async () => {
    if (!edit) return;
    const body = { name: edit.name, nuit: edit.nuit.trim() || null, phone: edit.phone, commissionPct: edit.pct, note: edit.note };
    const e = await attempt(async () => {
      if (edit.id) await patch(`/consignors/${edit.id}`, body);
      else setSel((await post<{ id: string }>('/consignors', body)).id);
    });
    setErr(e);
    if (!e) { toast('Fornecedor gravado'); setEdit(null); void list.reload(); }
  };
  const current = list.data.items.find((c) => c.id === sel) ?? null;
  return (
    <div className="max-w-6xl space-y-4">
      {err && <Alert>{err}</Alert>}
      <div className="grid gap-4 lg:grid-cols-[300px_minmax(0,1fr)]">
        <Card title="Fornecedores" padded={false} actions={<Button size="sm" icon={Plus} onClick={() => setEdit({ id: null, name: '', nuit: '', phone: '', pct: '30', note: '' })}>Novo</Button>}>
          {!list.data.items.length ? <EmptyState icon={Shirt} title="Sem fornecedores" text="Crie o fornecedor e, na ficha de cada peça dele, escolha-o em Consignação." /> : (
            <ul className="divide-y divide-[var(--border)]">
              {list.data.items.map((c) => (
                <li key={c.id}>
                  <button onClick={() => setSel(c.id)} className={clsx('w-full px-4 py-3 text-left hover:bg-surface-2', sel === c.id && 'bg-surface-2', !c.active && 'opacity-50')}>
                    <div className="font-medium">{c.name}</div>
                    <div className="text-xs text-muted">{c.commission_pct.replace('.', ',')}% para a loja · {c.products} peça{c.products === 1 ? '' : 's'}{c.settled_until ? ` · acertado até ${dmy(c.settled_until)}` : ''}</div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </Card>
        {current && <ConsignorDetail key={current.id} c={current} onEdit={() => setEdit({ id: current.id, name: current.name, nuit: current.nuit ?? '', phone: current.phone ?? '', pct: current.commission_pct.replace('.', ','), note: current.note ?? '' })} onChanged={() => void list.reload()} />}
      </div>
      {edit && (
        <Dialog title={edit.id ? 'Editar fornecedor' : 'Novo fornecedor'} onClose={() => setEdit(null)}
          footer={<><Button variant="ghost" onClick={() => setEdit(null)}>Cancelar</Button><Button disabled={!edit.name.trim()} onClick={() => void save()}>Gravar</Button></>}>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Nome" required className="sm:col-span-2"><Input autoFocus value={edit.name} maxLength={120} onChange={(e) => setEdit({ ...edit, name: e.target.value })} /></Field>
            <Field label="NUIT"><Input inputMode="numeric" maxLength={9} className="w-40" value={edit.nuit} onChange={(e) => setEdit({ ...edit, nuit: e.target.value.replace(/\D/g, '') })} /></Field>
            <Field label="Telefone"><Input inputMode="tel" value={edit.phone} onChange={(e) => setEdit({ ...edit, phone: e.target.value })} /></Field>
            <Field label="Parte da loja" required hint="Sobre o valor sem IVA de cada peça vendida"><Input inputMode="decimal" className="w-28" value={edit.pct} onChange={(e) => setEdit({ ...edit, pct: e.target.value })} /></Field>
            <Field label="Notas" className="sm:col-span-2"><Input value={edit.note} maxLength={300} onChange={(e) => setEdit({ ...edit, note: e.target.value })} placeholder="Conta para pagamento, prazo de devolução das peças…" /></Field>
          </div>
        </Dialog>
      )}
    </div>
  );
}

function ConsignorDetail({ c, onEdit, onChanged }: { c: Consignor; onEdit: () => void; onChanged: () => void }) {
  const rep = useLoad<ConsReport>(`/consignors/${c.id}/report`);
  const hist = useLoad<{ items: Settlement[] }>(`/consignors/${c.id}/settlements`);
  const [closing, setClosing] = useState<{ reference: string } | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const toast = useToast();
  const co = useLoad<{ company: { legalName: string; tradeName: string | null } }>('/settings/company');
  const company = co.data ? co.data.company.tradeName ?? co.data.company.legalName : '';
  const r = rep.data;
  const nothing = !r || r.from > r.to;
  return (
    <div className="space-y-4">
      {err && <Alert>{err}</Alert>}
      <Card title={c.name} description={nothing ? 'Tudo acertado até hoje.' : `Por acertar: de ${dmy(r!.from)} a ${dmy(r!.to)}`} padded={false}
        actions={<div className="flex gap-2">
          <Button variant="ghost" size="sm" onClick={onEdit}>Editar</Button>
          {r && !nothing && <Button variant="ghost" size="sm" onClick={() => void printHtml(settlementHtml(company, c, { from: r.from, to: r.to, lines: r.lines, totals: r.totals }))}>Imprimir</Button>}
          {r && !nothing && <Button size="sm" onClick={() => setClosing({ reference: '' })}>Fechar o acerto</Button>}
        </div>}>
        {rep.error ? <div className="p-4"><Alert>{rep.error}</Alert></div> : !r ? <div className="p-4"><Skeleton rows={3} /></div> : (
          <div className="overflow-x-auto p-2 xl:p-3">
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase tracking-wide text-muted">
                <tr><th className="px-2 py-2">Peça</th><th className="px-2 text-right">Vendidas</th><th className="hidden px-2 text-right sm:table-cell">Vendido (c/ IVA)</th><th className="hidden px-2 text-right md:table-cell">Sem IVA</th><th className="px-2 text-right">Loja</th><th className="px-2 text-right">A pagar</th><th className="hidden px-2 text-right md:table-cell">Em stock</th></tr>
              </thead>
              <tbody className="divide-y divide-[var(--border)]">
                {r.lines.map((l) => (
                  <tr key={l.productId} className={new D(l.quantity).isZero() ? 'text-muted' : ''}>
                    <td className="px-2 py-2"><div className="font-medium">{l.name}</div><div className="font-mono text-xs text-muted">{l.code} · {l.pct.replace('.', ',')}%</div></td>
                    <td className="num px-2 text-right">{q(l.quantity)}</td>
                    <td className="num hidden px-2 text-right sm:table-cell">{formatMoney(l.gross)}</td>
                    <td className="num hidden px-2 text-right md:table-cell">{formatMoney(l.net)}</td>
                    <td className="num px-2 text-right">{formatMoney(l.storeShare)}</td>
                    <td className="num px-2 text-right font-semibold">{formatMoney(l.due)}</td>
                    <td className="num hidden px-2 text-right md:table-cell">{q(l.stock)}</td>
                  </tr>
                ))}
                {!r.lines.length && <tr><td colSpan={7} className="py-8 text-center text-muted">Ainda não há peças deste fornecedor: escolha-o na ficha do artigo, em Consignação.</td></tr>}
              </tbody>
              {r.lines.length > 0 && (
                <tfoot className="border-t-2 border-base font-semibold">
                  <tr><td className="px-2 py-2">Total</td><td /><td className="num hidden px-2 text-right sm:table-cell">{formatMoney(r.totals.gross)}</td><td className="num hidden px-2 text-right md:table-cell">{formatMoney(r.totals.net)}</td><td className="num px-2 text-right">{formatMoney(r.totals.storeShare)}</td><td className="num px-2 text-right text-brand-700">{formatMoney(r.totals.due)}</td><td className="hidden md:table-cell" /></tr>
                </tfoot>
              )}
            </table>
          </div>
        )}
      </Card>
      {!!hist.data?.items.length && (
        <Card title="Acertos feitos" padded={false}>
          <ul className="divide-y divide-[var(--border)]">
            {hist.data.items.map((s) => (
              <li key={s.id} className="flex flex-wrap items-center gap-3 px-4 py-3 text-sm xl:px-6">
                <span className="min-w-0 flex-1"><b>N.º {s.number}</b> · {dmy(s.period_from)} a {dmy(s.period_to)}{s.reference ? ` · ${s.reference}` : ''}<span className="text-muted"> · {s.created_by_name ?? ''}</span></span>
                <span className="num">pago ao fornecedor <b>{formatMoney(s.due)}</b> · loja {formatMoney(s.store_share)}</span>
                <Button variant="ghost" size="sm" onClick={() => void printHtml(settlementHtml(company, c, { number: s.number, from: s.period_from, to: s.period_to, lines: s.lines, totals: { gross: s.gross, net: s.net, storeShare: s.store_share, due: s.due }, reference: s.reference }))}>Imprimir</Button>
              </li>
            ))}
          </ul>
        </Card>
      )}
      {closing && r && (
        <Dialog title="Fechar o acerto" size="sm" onClose={() => setClosing(null)}
          footer={<><Button variant="ghost" onClick={() => setClosing(null)}>Cancelar</Button><Button onClick={async () => {
            const e = await attempt(async () => { const x = await post<{ number: number }>(`/consignors/${c.id}/settlements`, { to: r.to, reference: closing.reference }); toast(`Acerto n.º ${x.number} fechado`); });
            setErr(e); setClosing(null);
            if (!e) { void rep.reload(); void hist.reload(); onChanged(); }
          }}>Fechar o acerto</Button></>}>
          <div className="space-y-4">
            <p>De {dmy(r.from)} a {dmy(r.to)}: pagar <b className="num">{formatMoney(r.totals.due)} MT</b> a {c.name}. O próximo acerto começa no dia seguinte.</p>
            <Field label="Referência (opcional)" hint="N.º da factura do fornecedor ou do pagamento"><Input value={closing.reference} maxLength={60} onChange={(e) => setClosing({ reference: e.target.value })} /></Field>
          </div>
        </Dialog>
      )}
    </div>
  );
}
